import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { archetypeConfig } from "@/lib/archetypeConfig";

const createStyles = (primaryColor: string = "#3b82f6") => StyleSheet.create({
  page: {
    padding: 40,
    paddingBottom: 60,
    fontFamily: "Helvetica",
    fontSize: 9,
    lineHeight: 1.7,
  },
  // Cover/Executive page - more whitespace
  coverPage: {
    padding: 48,
    paddingBottom: 60,
    fontFamily: "Helvetica",
    fontSize: 9,
    lineHeight: 1.7,
  },
  header: {
    marginBottom: 16,
    borderBottom: `2 solid ${primaryColor}`,
    paddingBottom: 10,
  },
  // H1 - Visually dominant
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 4,
    color: "#111827",
    letterSpacing: 0.3,
  },
  subtitle: {
    fontSize: 8,
    color: "#6b7280",
    letterSpacing: 0.2,
  },
  // H2 - Clearly smaller but distinct
  sectionTitle: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 8,
    marginTop: 4,
    color: "#1f2937",
    letterSpacing: 0.2,
  },
  // Body text - More breathing room
  text: {
    fontSize: 9,
    marginBottom: 4,
    color: "#374151",
    lineHeight: 1.7,
  },
  section: {
    marginBottom: 16,
  },
  bullet: {
    flexDirection: "row",
    marginBottom: 3,
  },
  bulletText: {
    flex: 1,
    fontSize: 8,
    color: "#4b5563",
    lineHeight: 1.6,
  },
  // Decision Mark - THE visual anchor
  decisionMark: {
    padding: 16,
    backgroundColor: primaryColor,
    borderRadius: 6,
    marginBottom: 20,
    marginTop: 12,
  },
  decisionMarkLabel: {
    color: "#ffffff",
    fontSize: 8,
    opacity: 0.9,
    marginBottom: 4,
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  decisionMarkText: {
    color: "#ffffff",
    fontSize: 14,
    fontWeight: "bold",
    letterSpacing: 0.3,
  },
  // Meta context box
  metaContext: {
    padding: 10,
    backgroundColor: "#f9fafb",
    borderRadius: 4,
    marginBottom: 16,
    borderLeft: "3 solid #e5e7eb",
  },
  // Confidence explanation
  confidenceBox: {
    padding: 10,
    backgroundColor: "#eff6ff",
    borderRadius: 4,
    marginTop: 16,
    borderLeft: `3 solid ${primaryColor}`,
  },
  highlightBox: {
    padding: 10,
    backgroundColor: "#dbeafe",
    borderRadius: 4,
    marginBottom: 8,
    borderLeft: `3 solid ${primaryColor}`,
  },
  successBox: {
    padding: 8,
    backgroundColor: "#dcfce7",
    borderRadius: 4,
    marginBottom: 4,
    borderLeft: "2 solid #22c55e",
  },
  warningBox: {
    padding: 8,
    backgroundColor: "#fef3c7",
    borderRadius: 4,
    marginBottom: 4,
    borderLeft: "2 solid #f59e0b",
  },
  dangerBox: {
    padding: 8,
    backgroundColor: "#fee2e2",
    borderRadius: 4,
    marginBottom: 4,
    borderLeft: "2 solid #ef4444",
  },
  gridRow: {
    flexDirection: "row",
    borderBottom: "1 solid #e5e7eb",
    padding: 6,
  },
  gridCell: {
    flex: 1,
    fontSize: 8,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 7,
    borderTop: "1 solid #e5e7eb",
    paddingTop: 6,
  },
  heroBox: {
    padding: 12,
    backgroundColor: primaryColor,
    borderRadius: 5,
    marginBottom: 12,
  },
  heroText: {
    color: "#ffffff",
    fontSize: 12,
    fontWeight: "bold",
  },
  heroLabel: {
    color: "#ffffff",
    fontSize: 8,
    opacity: 0.9,
    marginBottom: 2,
  },
  thresholdBox: {
    padding: 8,
    borderRadius: 4,
    flex: 1,
    marginHorizontal: 3,
  },
  weekBox: {
    padding: 10,
    backgroundColor: "#f9fafb",
    borderRadius: 4,
    marginBottom: 6,
  },
  weekTitle: {
    fontSize: 9,
    fontWeight: "bold",
    marginBottom: 4,
    color: "#374151",
  },
  taskItem: {
    fontSize: 8,
    marginBottom: 3,
    color: "#4b5563",
    lineHeight: 1.5,
  },
  kpiItem: {
    fontSize: 7,
    color: "#d97706",
    marginBottom: 2,
  },
  commitmentBox: {
    padding: 12,
    backgroundColor: "#eff6ff",
    borderRadius: 4,
    marginBottom: 8,
    borderLeft: `3 solid ${primaryColor}`,
  },
  excludedBox: {
    padding: 6,
    backgroundColor: "#f3f4f6",
    borderRadius: 3,
    marginBottom: 3,
    borderLeft: "2 solid #9ca3af",
  },
  // Phase 1 Criteria Styles
  criteriaCard: {
    padding: 10,
    backgroundColor: "#f9fafb",
    borderRadius: 4,
    marginBottom: 8,
    borderLeft: `2 solid ${primaryColor}`,
  },
  criteriaLabel: {
    fontSize: 7,
    color: "#6b7280",
    marginBottom: 3,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  criteriaValue: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#1f2937",
    lineHeight: 1.5,
  },
  validatorBadge: {
    padding: 4,
    backgroundColor: "#dbeafe",
    borderRadius: 3,
    marginRight: 5,
    marginBottom: 3,
  },
  conflictBox: {
    padding: 8,
    backgroundColor: "#fef3c7",
    borderRadius: 4,
    marginBottom: 6,
    borderLeft: "2 solid #f59e0b",
  },
  costItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 3,
  },
  // Decision Snapshot Styles
  snapshotBox: {
    padding: 10,
    backgroundColor: "#f0f9ff",
    borderRadius: 4,
    marginBottom: 8,
    borderLeft: `3 solid ${primaryColor}`,
  },
  snapshotLabel: {
    fontSize: 7,
    color: "#6b7280",
    marginBottom: 3,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  snapshotValue: {
    fontSize: 9,
    color: "#1f2937",
    lineHeight: 1.6,
  },
  // Dominant Strategy Styles
  dominantBox: {
    padding: 12,
    backgroundColor: "#f0fdf4",
    borderRadius: 4,
    marginBottom: 8,
    borderLeft: "3 solid #22c55e",
  },
  rationaleItem: {
    flexDirection: "row",
    marginBottom: 4,
  },
  // Strategic Path Styles
  pathStep: {
    flexDirection: "row",
    marginBottom: 8,
  },
  pathCircle: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: "#dbeafe",
    marginRight: 10,
  },
  pathLine: {
    width: 1,
    height: 14,
    backgroundColor: "#e5e7eb",
    marginLeft: 8,
  },
  // Outcome layers (3-tier structure for key findings)
  outcomeDecision: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: 8,
  },
  outcomeBullets: {
    marginBottom: 8,
  },
  outcomeDetail: {
    fontSize: 8,
    color: "#6b7280",
    lineHeight: 1.6,
  },
});

interface ReportPDFProps {
  report: any;
  branding?: {
    primary_color?: string;
    logo_url?: string;
  };
  sessionMeta?: any;
  // Governance props
  sessionId?: string;
  commitHash?: string;
  commitType?: string;
  committedAt?: string;
  reportId?: string;
  versionNumber?: number;
  inputsHash?: string;
  modelVersion?: string;
  mode?: 'board' | 'operational';
}

const formatCriterionLabel = (key: string, matrixView?: any[]): string => {
  if (matrixView) {
    const entry = matrixView.find((row: any) => row.criterion === key);
    if (entry?.label) return entry.label;
  }
  
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Format cost level to German label
const formatCostLevel = (level: string): string => {
  switch (level) {
    case "high": return "Hoch";
    case "medium": return "Mittel";
    case "low": return "Gering";
    default: return level;
  }
};

// Validator hierarchy labels
const VALIDATOR_LABELS: Record<string, { label: string; priority: number }> = {
  "data": { label: "Daten / Metriken", priority: 1 },
  "customers": { label: "Kunden", priority: 2 },
  "board": { label: "Board / Investoren", priority: 3 },
  "team": { label: "Internes Team", priority: 4 },
};

// Clean text from encoding artifacts (Silbentrennung fix)
const cleanText = (text: string | undefined | null): string => {
  if (!text) return "";
  // Remove soft hyphens and other encoding artifacts
  return text
    .replace(/\u00AD/g, '') // soft hyphen
    .replace(/\uFFFD/g, '') // replacement character
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // zero-width chars
    .replace(/\s+/g, ' ') // normalize whitespace
    .trim();
};

// Truncate text to max lines (approx 80 chars per line)
const truncateText = (text: string | undefined | null, maxChars: number = 400): string => {
  const cleaned = cleanText(text);
  if (cleaned.length <= maxChars) return cleaned;
  return cleaned.substring(0, maxChars).trim() + "...";
};

export const ReportPDF = ({ report, branding, sessionMeta, sessionId, commitHash, commitType, committedAt, reportId, versionNumber, inputsHash, modelVersion, mode = 'operational' }: ReportPDFProps) => {
  const primaryColor = branding?.primary_color || "#3b82f6";
  const styles = createStyles(primaryColor);
  
  // Sichere Extraktion mit Fallbacks
  const executiveSummary = report?.executive_summary || {};
  const problemContext = report?.problem_and_context || {};
  const optionsOverview = report?.options_overview || {};
  const comparisonMatrix = report?.comparison_matrix_summary || {};
  const recommendation = report?.recommendation || {};
  const actionRoadmap = report?.action_roadmap || {};
  const goDecision = report?.go_decision || report?.metadata?.go_decision || {};
  // Failure analysis can be in multiple places
  const failureAnalysis = report?.failure_analysis || 
    report?.action_roadmap?.failure_analysis ||
    report?.metadata?.failure_analysis || 
    {};
  const metadata = report?.metadata || {};

  // Team Perspectives aus metadata
  const teamPerspectives = metadata?.team_perspectives || null;
  const hasTeamPerspectives = teamPerspectives && (
    (Array.isArray(teamPerspectives.consensus_areas) && teamPerspectives.consensus_areas.length > 0) ||
    (Array.isArray(teamPerspectives.divergence_areas) && teamPerspectives.divergence_areas.length > 0) ||
    (Array.isArray(teamPerspectives.blocking_issues) && teamPerspectives.blocking_issues.length > 0) ||
    teamPerspectives.synthesis
  );

  // Commitment-Daten aus sessionMeta.decision_commit (CRITICAL: nicht sessionMeta.commitment!)
  const commitment = sessionMeta?.decision_commit || metadata?.decision_commit || {};

  // Decision Snapshot Daten
  const decisionSnapshot = {
    problem_statement: cleanText(problemContext?.problem_statement || sessionMeta?.problem_statement),
    decision_question: cleanText(problemContext?.decision_question_original || problemContext?.decision_question),
    primary_tension: cleanText(problemContext?.primary_tension),
    primary_hypothesis: cleanText(sessionMeta?.central_hypothesis || metadata?.central_hypothesis),
  };

  // Dominant Strategy Daten
  const dominantStrategy = {
    recommended_option_label: cleanText(recommendation?.recommended_option_label || executiveSummary?.recommended_option_label),
    decision_rationale: cleanText(recommendation?.reasoning?.primary_argument),
    rationale: (recommendation?.reasoning?.supporting_factors || []).map(cleanText),
    risks: recommendation?.risks || [],
    risk_mitigation: (recommendation?.risk_mitigation || []).map(cleanText),
  };

  // Strategic Path Daten
  const strategicPath = {
    current_phase: cleanText(recommendation?.current_phase || actionRoadmap?.current_phase),
    next_decision_point: cleanText(recommendation?.next_decision_point || actionRoadmap?.next_decision_point),
    transition_triggers: (recommendation?.transition_triggers || actionRoadmap?.transition_triggers || []).map(cleanText),
    long_term_implications: cleanText(recommendation?.long_term_implications || actionRoadmap?.long_term_implications),
  };

  // Phase 1.1 Erfolgskriterien
  const successCriteria = {
    signal: cleanText(sessionMeta?.success_signals?.[0] || metadata?.success_signals?.[0]),
    timeframe: cleanText(sessionMeta?.success_timeframe || metadata?.success_timeframe),
    validators: sessionMeta?.success_validators || metadata?.success_validators || [],
    notes: cleanText(sessionMeta?.success_notes || metadata?.success_notes),
  };

  // Phase 1.2 Konfliktdaten
  const conflictData = {
    strategyA: cleanText(sessionMeta?.conflict_strategy_a || metadata?.conflict_strategy_a),
    strategyB: cleanText(sessionMeta?.conflict_strategy_b || metadata?.conflict_strategy_b),
    costs: sessionMeta?.conflict_costs || metadata?.conflict_costs,
  };

  // Phase 1.3 Hypothese
  const hypothesisData = {
    hypothesis: cleanText(sessionMeta?.central_hypothesis || metadata?.central_hypothesis),
    falsification: cleanText(sessionMeta?.hypothesis_falsification || metadata?.hypothesis_falsification),
  };

  // Phase 1.4 Killer-Annahme
  const killerAssumptionData = {
    assumption: cleanText(sessionMeta?.killer_assumption || metadata?.killer_assumption),
    falsification: cleanText(sessionMeta?.killer_falsification || metadata?.killer_falsification),
  };

  // Check if Phase 1 data exists
  const hasPhase1Data = successCriteria.signal || successCriteria.timeframe || 
    successCriteria.validators.length > 0 || conflictData.strategyA || conflictData.strategyB ||
    hypothesisData.hypothesis || killerAssumptionData.assumption;

  // Check if strategic path data exists
  const hasStrategicPathData = strategicPath.current_phase || strategicPath.next_decision_point || 
    strategicPath.transition_triggers.length > 0 || strategicPath.long_term_implications;

  const shortSessionId = sessionId ? sessionId.substring(0, 8) : '—';
  const shortCommitHash = commitHash ? commitHash.substring(0, 8) : null;
  const generatedDate = new Date(committedAt || metadata.generated_at || Date.now()).toLocaleDateString("de-DE", { day: '2-digit', month: 'short', year: 'numeric' });

  const Footer = () => (
    <View style={styles.footer} fixed>
      <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 2 }}>
        <Text style={{ fontSize: 6, color: "#9ca3af" }}>
          {`Quentari DecisionOS  ·  Report v${versionNumber || 1}  ·  Session ${shortSessionId}`}
        </Text>
        <Text render={({ pageNumber, totalPages }) => `Seite ${pageNumber} / ${totalPages}`} style={{ fontSize: 6, color: "#9ca3af" }} />
      </View>
      <Text style={{ fontSize: 5.5, color: "#b0b0b0", textAlign: "center" }}>
        {shortCommitHash ? `Commit ${shortCommitHash}  ·  ` : ''}{`Generated ${generatedDate}  ·  Confidential — For Internal Strategic Use Only`}
      </Text>
    </View>
  );

  const introParagraph = cleanText(
    executiveSummary.intro_paragraph || 
    executiveSummary.executive_intro_paragraph ||
    (Array.isArray(executiveSummary.key_insights) ? executiveSummary.key_insights.join(' ') : '') ||
    (Array.isArray(executiveSummary.key_points) ? executiveSummary.key_points.join(' ') : '')
  );
  
  const options = Array.isArray(optionsOverview?.options) ? optionsOverview.options : [];
  const matrixView = Array.isArray(comparisonMatrix?.matrix_view) ? comparisonMatrix.matrix_view : [];
  const optionTotals = Array.isArray(comparisonMatrix?.option_totals) ? comparisonMatrix.option_totals : [];
  
  // Build weeks array from week_1, week_2, etc. structure (matching ActionPlanSection logic)
  const weeks: Array<{ focus: string; tasks: string[]; kpis?: string[]; decision_point?: string }> = [];
  if (actionRoadmap?.week_1) weeks.push(actionRoadmap.week_1);
  if (actionRoadmap?.week_2) weeks.push(actionRoadmap.week_2);
  if (actionRoadmap?.week_3) weeks.push(actionRoadmap.week_3);
  if (actionRoadmap?.week_4) weeks.push(actionRoadmap.week_4);
  
  // Fallback to legacy weeks array if present
  if (weeks.length === 0 && Array.isArray(actionRoadmap?.weeks)) {
    weeks.push(...actionRoadmap.weeks);
  }
  
  // Fallback to horizon_90_days structure
  if (weeks.length === 0 && Array.isArray(actionRoadmap?.horizon_90_days)) {
    const actions = actionRoadmap.horizon_90_days;
    const chunkSize = Math.ceil(actions.length / 4);
    for (let i = 0; i < 4; i++) {
      const chunk = actions.slice(i * chunkSize, (i + 1) * chunkSize);
      if (chunk.length > 0) {
        weeks.push({
          focus: cleanText(chunk[0]?.headline) || `Woche ${i + 1}`,
          tasks: chunk.map((item: any) => cleanText(item.headline || item)),
          kpis: [],
        });
      }
    }
  }
  
  // Hypotheses to test
  const hypothesesToTest = (actionRoadmap?.hypotheses_to_test || []).map(cleanText);
  
  const alternativeOptions = options.filter((opt: any) => opt.option_id !== recommendation?.recommended_option_id);

  // Confidence level and explanation
  const confidenceLevel = executiveSummary.confidence_level || 0;
  const getConfidenceExplanation = (level: number): string => {
    if (level >= 80) return "Hohe Datenbasis, klare Präferenz. Re-Evaluation nur bei fundamentalen Marktveränderungen.";
    if (level >= 60) return "Solide Grundlage mit offenen Punkten. Re-Evaluation empfohlen nach 30 Tagen.";
    if (level >= 40) return "Begrenzte Datenbasis. Empfehlung basiert auf besten verfügbaren Informationen.";
    return "Explorative Entscheidung. Validierung der Kernannahmen hat Priorität.";
  };

  return (
    <Document>
      {/* PAGE 1 - EXECUTIVE SUMMARY (eigene Seite, max Whitespace) */}
      <Page size="A4" style={styles.coverPage} wrap>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Decision Report</Text>
          <Text style={styles.subtitle}>
            {new Date(metadata.generated_at || Date.now()).toLocaleDateString("de-DE", { 
              day: '2-digit', 
              month: 'long', 
              year: 'numeric' 
            })}
          </Text>
        </View>

        {/* Meta Context - For whom? */}
        <View style={styles.metaContext}>
          <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>
            Zweck
          </Text>
          <Text style={{ fontSize: 8, color: "#374151" }}>
            Strategische Entscheidungsvorlage für Führungskräfte und Entscheidungsträger.
          </Text>
        </View>

        {/* THE DECISION MARK - Big, clear, unmistakable */}
        {dominantStrategy.recommended_option_label && (
          <View style={styles.decisionMark}>
            <Text style={styles.decisionMarkLabel}>DECISION</Text>
            <Text style={styles.decisionMarkText}>
              {dominantStrategy.recommended_option_label}
            </Text>
          </View>
        )}

        {/* 3-Tier Outcome Structure */}
        {introParagraph && (
          <View style={styles.section}>
            {/* Tier 1: One sentence decision outcome */}
            <Text style={styles.outcomeDecision}>
              {truncateText(introParagraph, 200)}
            </Text>

            {/* Tier 2: 3 bullets - what changes visibly */}
            {dominantStrategy.rationale.length > 0 && (
              <View style={styles.outcomeBullets}>
                {dominantStrategy.rationale.slice(0, 3).map((point: string, idx: number) => (
                  <View key={idx} style={{ flexDirection: "row", marginBottom: 4 }}>
                    <Text style={{ fontSize: 8, color: primaryColor, marginRight: 6 }}>•</Text>
                    <Text style={{ fontSize: 8, color: "#374151", flex: 1, lineHeight: 1.5 }}>
                      {truncateText(point, 120)}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        )}

        {/* Decision Question */}
        {decisionSnapshot.decision_question && (
          <View style={styles.highlightBox}>
            <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
              Kernfrage der Entscheidung
            </Text>
            <Text style={{ fontSize: 10, fontWeight: "bold", color: "#1f2937", lineHeight: 1.5 }}>
              "{decisionSnapshot.decision_question}"
            </Text>
          </View>
        )}

        {/* Classification Provenance */}
        {metadata?.classification_provenance && (
          <View style={{ padding: 10, backgroundColor: "#f9fafb", borderRadius: 4, marginBottom: 8, borderLeft: "2 solid #9ca3af" }}>
            <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 6 }}>
              <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Klassifizierungs-Provenance
              </Text>
              <Text style={{ fontSize: 7, fontWeight: "bold", color: metadata.classification_provenance.source === 'user_override' ? "#d97706" : "#059669" }}>
                {metadata.classification_provenance.source === 'user_override' ? "User-Override" : "System bestätigt"}
              </Text>
            </View>
            <View style={{ flexDirection: "row", marginBottom: 4 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 7, color: "#6b7280", marginBottom: 2 }}>System-Vorschlag</Text>
                <Text style={{ fontSize: 8, color: "#1f2937" }}>
                  {metadata.classification_provenance.system_suggestion?.type} / {metadata.classification_provenance.system_suggestion?.subtype}
                </Text>
                <Text style={{ fontSize: 7, color: "#6b7280" }}>
                  Konfidenz: {metadata.classification_provenance.system_suggestion?.confidence}%
                </Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 7, color: "#6b7280", marginBottom: 2 }}>Finale Auswahl</Text>
                <Text style={{ fontSize: 8, color: "#1f2937" }}>
                  {metadata.classification_provenance.final?.type} / {metadata.classification_provenance.final?.subtype}
                </Text>
              </View>
            </View>
            {metadata.classification_provenance.system_suggestion?.model && (
              <Text style={{ fontSize: 6, color: "#9ca3af", marginTop: 2 }}>
                Modell: {metadata.classification_provenance.system_suggestion.model}
                {metadata.classification_provenance.system_suggestion.input_hash ? ` · Hash: ${metadata.classification_provenance.system_suggestion.input_hash.slice(0, 12)}…` : ""}
              </Text>
            )}
          </View>
        )}

        {/* Confidence with Explanation */}
        <View style={styles.confidenceBox}>
          <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 6 }}>
            <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Entscheidungskonfidenz
            </Text>
            <Text style={{ fontSize: 12, fontWeight: "bold", color: primaryColor }}>
              {confidenceLevel}%
            </Text>
          </View>
          <Text style={{ fontSize: 8, color: "#374151", lineHeight: 1.5 }}>
            {getConfidenceExplanation(confidenceLevel)}
          </Text>
        </View>

        {/* One-Sentence-Conclusion */}
        {executiveSummary?.one_sentence_conclusion && (
          <View style={{ marginTop: 12, paddingTop: 8, borderTop: "1 solid #e5e7eb" }}>
            <Text style={{ fontSize: 9, fontStyle: "italic", color: "#374151", lineHeight: 1.6 }}>
              {cleanText(executiveSummary.one_sentence_conclusion)}
            </Text>
          </View>
        )}

        {/* Bias-Reflexion */}
        {metadata?.bias_reflection?.initial_preference && (
          <View style={[styles.warningBox, { marginTop: 12, backgroundColor: metadata.bias_reflection.recommendation_matches ? "#fff7ed" : "#f0fdf4", borderLeftColor: metadata.bias_reflection.recommendation_matches ? "#f59e0b" : "#22c55e" }]}>
            <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
              Bias-Reflexion
            </Text>
            <View style={{ flexDirection: "row", marginBottom: 4 }}>
              <Text style={{ fontSize: 7, color: "#374151" }}>
                Initiale Tendenz: {cleanText(metadata.bias_reflection.initial_preference_label)}  →  Empfehlung: {cleanText(dominantStrategy.recommended_option_label)}
              </Text>
            </View>
            <Text style={{ fontSize: 8, color: "#374151", lineHeight: 1.5 }}>
              {cleanText(metadata.bias_reflection.reflection_text || (metadata.bias_reflection.recommendation_matches ? "Empfehlung bestätigt initiale Tendenz – Confirmation Bias beachten." : "Empfehlung weicht von initialer Tendenz ab – analytische Korrektur."))}
            </Text>
          </View>
        )}

        {/* Strategischer Rahmen */}
        {(sessionMeta?.decision_thesis || sessionMeta?.phase1_3_irreversibility || sessionMeta?.impact_score != null) && (
          <View style={[styles.criteriaCard, { marginTop: 12 }]}>
            <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
              Strategischer Rahmen
            </Text>
            {/* Decision Thesis */}
            {sessionMeta?.decision_thesis && (
              <Text style={{ fontSize: 10, fontWeight: "bold", color: "#1f2937", lineHeight: 1.5, marginBottom: 8 }}>
                {cleanText(typeof sessionMeta.decision_thesis === 'string' ? sessionMeta.decision_thesis : sessionMeta.decision_thesis?.thesis)}
              </Text>
            )}
            {/* Commit Mode + Kill Criterion */}
            <View style={{ flexDirection: "row", marginBottom: 6 }}>
              {sessionMeta?.commit_mode != null && (
                <View style={{ backgroundColor: sessionMeta.commit_mode ? "#dbeafe" : "#f3e8ff", padding: 3, borderRadius: 2, marginRight: 8 }}>
                  <Text style={{ fontSize: 7, fontWeight: "bold", color: sessionMeta.commit_mode ? "#1d4ed8" : "#7c3aed" }}>
                    {sessionMeta.commit_mode ? "COMMIT" : "EXPLORE"}
                  </Text>
                </View>
              )}
              {sessionMeta?.kill_criterion && (
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 7, color: "#991b1b" }}>
                    Kill: {cleanText(sessionMeta.kill_criterion)}
                  </Text>
                </View>
              )}
            </View>
            {/* Irreversibility Compact */}
            {sessionMeta?.phase1_3_irreversibility && (() => {
              const irr = sessionMeta.phase1_3_irreversibility;
              return (
                <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 6, gap: 4 }}>
                  {irr.structural_urgency_score != null && (
                    <View style={{ backgroundColor: "#fef3c7", padding: 3, borderRadius: 2 }}>
                      <Text style={{ fontSize: 7, color: "#92400e" }}>Urgency: {irr.structural_urgency_score}/10</Text>
                    </View>
                  )}
                  {irr.point_of_no_return_month && (
                    <View style={{ backgroundColor: "#fee2e2", padding: 3, borderRadius: 2 }}>
                      <Text style={{ fontSize: 7, color: "#991b1b" }}>PoNR: {irr.point_of_no_return_month}</Text>
                    </View>
                  )}
                  {irr.daily_exposure_eur != null && (
                    <View style={{ backgroundColor: "#fef3c7", padding: 3, borderRadius: 2 }}>
                      <Text style={{ fontSize: 7, color: "#92400e" }}>Burn: {irr.daily_exposure_eur}€/Tag</Text>
                    </View>
                  )}
                  {Array.isArray(irr.lock_in_types) && irr.lock_in_types.length > 0 && (
                    <View style={{ backgroundColor: "#f3f4f6", padding: 3, borderRadius: 2 }}>
                      <Text style={{ fontSize: 7, color: "#374151" }}>Lock-in: {irr.lock_in_types.join(", ")}</Text>
                    </View>
                  )}
                </View>
              );
            })()}
            {/* Impact / Urgency */}
            <View style={{ flexDirection: "row", marginBottom: 6 }}>
              {(sessionMeta?.impact_score != null || report?.metadata?.user_context?.impact_score != null) && (
                <View style={{ marginRight: 16 }}>
                  <Text style={{ fontSize: 7, color: "#6b7280" }}>Impact</Text>
                  <Text style={{ fontSize: 11, fontWeight: "bold", color: "#1f2937" }}>{sessionMeta?.impact_score ?? report?.metadata?.user_context?.impact_score}/10</Text>
                </View>
              )}
              {(sessionMeta?.urgency_score != null || report?.metadata?.user_context?.urgency_score != null) && (
                <View>
                  <Text style={{ fontSize: 7, color: "#6b7280" }}>Urgency</Text>
                  <Text style={{ fontSize: 11, fontWeight: "bold", color: "#1f2937" }}>{sessionMeta?.urgency_score ?? report?.metadata?.user_context?.urgency_score}/10</Text>
                </View>
              )}
            </View>
            {/* Worst-Case */}
            {sessionMeta?.worst_case && (
              <View style={[styles.dangerBox, { marginTop: 4 }]}>
                <Text style={{ fontSize: 7, color: "#991b1b", fontWeight: "bold", marginBottom: 2 }}>
                  Worst-Case {sessionMeta.worst_case_probability ? `(${sessionMeta.worst_case_probability}%)` : ""}
                </Text>
                <Text style={{ fontSize: 8, color: "#991b1b", lineHeight: 1.5 }}>
                  {truncateText(sessionMeta.worst_case, 200)}
                </Text>
              </View>
            )}
          </View>
        )}

        <Footer />
      </Page>

      {/* PAGE 2 - Options & Matrix (visual decision center) */}
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header}>
          <Text style={styles.title}>Optionen & Bewertung</Text>
          <Text style={styles.subtitle}>Strukturierte Entscheidungsgrundlage</Text>
        </View>

        {/* Options - Compact Cards */}
        {options.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Strategische Optionen</Text>
            <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
              {options.slice(0, 3).map((option: any, idx: number) => {
                const isWinner = option.option_id === recommendation.recommended_option_id;
                const archetype = option.archetype || "low_risk";
                const archetypeInfo = archetypeConfig[archetype];
                const archetypeLabel = archetypeInfo?.label || archetype;
                const isWarningArchetype = archetypeInfo?.isWarning || false;
                
                return (
                  <View key={idx} style={{ 
                    width: "31%", 
                    padding: 8, 
                    marginRight: "2%", 
                    marginBottom: 6,
                    backgroundColor: isWarningArchetype ? "#fef2f2" : "#f9fafb", 
                    borderRadius: 4,
                    borderLeft: isWinner ? `3 solid ${primaryColor}` : isWarningArchetype ? "3 solid #ef4444" : "none"
                  }}>
                    <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 4 }}>
                      <Text style={{ fontSize: 8, fontWeight: "bold", flex: 1, color: "#1f2937" }}>
                        {cleanText(option.label) || `Option ${idx + 1}`}
                      </Text>
                    </View>
                    <Text style={{ 
                      fontSize: 6, 
                      color: isWarningArchetype ? "#ef4444" : "#6b7280", 
                      backgroundColor: isWarningArchetype ? "#fee2e2" : "#e5e7eb", 
                      padding: 2, 
                      borderRadius: 2,
                      alignSelf: "flex-start",
                      marginBottom: 4
                    }}>
                      {archetypeLabel}
                    </Text>
                    {option.strategic_goal && (
                      <Text style={{ fontSize: 7, color: "#6b7280", marginBottom: 3, lineHeight: 1.4 }}>
                        {truncateText(option.strategic_goal, 80)}
                      </Text>
                    )}
                    {Array.isArray(option.key_advantages) && option.key_advantages[0] && (
                      <Text style={{ fontSize: 7, color: "#16a34a" }}>+ {truncateText(option.key_advantages[0], 60)}</Text>
                    )}
                    {Array.isArray(option.key_risks) && option.key_risks[0] && (
                      <Text style={{ fontSize: 7, color: "#d97706" }}>- {truncateText(option.key_risks[0], 60)}</Text>
                    )}
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* Weighted Matrix - THE visual decision center */}
        {matrixView.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Bewertungsmatrix</Text>
            <Text style={{ fontSize: 7, color: "#6b7280", marginBottom: 8 }}>
              Gewichtete Bewertung auf Skala 1-3 (Niedrig/Mittel/Hoch)
            </Text>
            <View style={{ borderRadius: 4, overflow: "hidden", border: "1 solid #e5e7eb" }}>
              {/* Header Row */}
              <View style={[styles.gridRow, { backgroundColor: "#f3f4f6", borderBottom: "2 solid #e5e7eb" }]}>
                <Text style={[styles.gridCell, { fontWeight: "bold", fontSize: 7 }]}>Kriterium</Text>
                <Text style={[styles.gridCell, { fontWeight: "bold", fontSize: 7, textAlign: "center", flex: 0.5 }]}>Gew.</Text>
                {optionTotals.map((opt: any, idx: number) => (
                  <Text key={idx} style={[styles.gridCell, { fontWeight: "bold", textAlign: "center", fontSize: 7 }]}>
                    {cleanText(opt.label || `Opt ${idx + 1}`).substring(0, 14)}
                  </Text>
                ))}
              </View>
              {/* Data Rows */}
              {matrixView.map((row: any, idx: number) => (
                <View key={idx} style={[styles.gridRow, { backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f9fafb" }]}>
                  <Text style={[styles.gridCell, { fontSize: 7 }]}>
                    {cleanText(row.label || formatCriterionLabel(row.criterion, matrixView))}
                  </Text>
                  <Text style={[styles.gridCell, { fontSize: 7, textAlign: "center", flex: 0.5 }]}>{row.weight || 25}%</Text>
                  {Array.isArray(row.scores) && row.scores.map((score: any, sIdx: number) => (
                    <Text key={sIdx} style={[styles.gridCell, { textAlign: "center", fontSize: 8, fontWeight: "bold" }]}>
                      {score?.points ?? "-"}
                    </Text>
                  ))}
                </View>
              ))}
              {/* Total Row */}
              <View style={[styles.gridRow, { backgroundColor: "#dbeafe", borderTop: "2 solid #93c5fd" }]}>
                <Text style={[styles.gridCell, { fontWeight: "bold", fontSize: 8 }]}>Gesamt</Text>
                <Text style={[styles.gridCell, { fontSize: 7, flex: 0.5 }]}></Text>
                {optionTotals.map((opt: any, idx: number) => (
                  <Text key={idx} style={[styles.gridCell, { fontWeight: "bold", textAlign: "center", fontSize: 9, color: primaryColor }]}>
                    {(opt.weighted_total ?? opt.total_points ?? 0).toFixed ? (opt.weighted_total ?? opt.total_points).toFixed(1) : "-"}
                  </Text>
                ))}
              </View>
            </View>
          </View>
        )}

        {/* Tension/Conflict - Compact */}
        {decisionSnapshot.primary_tension && (
          <View style={[styles.warningBox, { marginTop: 8 }]}>
            <Text style={{ fontSize: 7, color: "#92400e", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>
              Kernspannung
            </Text>
            <Text style={{ fontSize: 8, color: "#1f2937", lineHeight: 1.5 }}>
              {truncateText(decisionSnapshot.primary_tension, 200)}
            </Text>
          </View>
        )}

        <Footer />
      </Page>

      {/* PAGE 3 - Dominant Strategy & Strategic Path */}
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header}>
          <Text style={styles.title}>Strategische Empfehlung</Text>
          <Text style={styles.subtitle}>Begründung und Umsetzungspfad</Text>
        </View>

        {/* DOMINANT STRATEGY */}
        {dominantStrategy.recommended_option_label && (
          <View style={styles.section}>
            <View style={styles.dominantBox}>
              <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 6 }}>
                <Text style={{ fontSize: 7, color: "#166534", textTransform: "uppercase", letterSpacing: 0.5 }}>Empfohlene Option</Text>
              </View>
              <Text style={{ fontSize: 11, fontWeight: "bold", color: "#166534", marginBottom: 8 }}>
                {dominantStrategy.recommended_option_label}
              </Text>
              
            {/* Constraint Warnings */}
              {(() => {
                const recOptId = recommendation?.recommended_option_id;
                const recOption = options.find((o: any) => o.option_id === recOptId);
                const isConstraintBreak = recOption?.is_constraint_break;
                const isStrategyShift = recOption?.is_strategy_shift;
                if (!isConstraintBreak && !isStrategyShift) return null;
                return (
                  <View style={[isConstraintBreak ? styles.dangerBox : styles.warningBox, { marginBottom: 8 }]}>
                    <Text style={{ fontSize: 8, fontWeight: "bold", color: isConstraintBreak ? "#991b1b" : "#92400e", marginBottom: 2 }}>
                      {isConstraintBreak ? "⚠ Constraint-Bruch" : "⚡ Strategiewechsel"}
                    </Text>
                    <Text style={{ fontSize: 7, color: isConstraintBreak ? "#991b1b" : "#92400e", lineHeight: 1.5 }}>
                      {isConstraintBreak
                        ? "Diese Option bricht die in Phase 1 definierten Constraints. Besondere Begründung erforderlich."
                        : "Diese Option weicht signifikant vom Phase-1-Commitment ab. Review empfohlen."}
                    </Text>
                  </View>
                );
              })()}

              {/* Decision Rationale - Declarative, not explanatory */}
              {dominantStrategy.decision_rationale && (
                <Text style={{ fontSize: 9, color: "#1f2937", lineHeight: 1.6, marginBottom: 8 }}>
                  {truncateText(dominantStrategy.decision_rationale, 250)}
                </Text>
              )}
            </View>

            {/* Risks & Mitigation - Trigger logic, less text */}
            {(dominantStrategy.risks.length > 0 || dominantStrategy.risk_mitigation.length > 0) && (
              <View style={{ flexDirection: "row", marginTop: 8 }}>
                {dominantStrategy.risks.length > 0 && (
                  <View style={[styles.warningBox, { flex: 1, marginRight: 6 }]}>
                    <Text style={{ fontSize: 7, fontWeight: "bold", color: "#92400e", marginBottom: 4, textTransform: "uppercase" }}>Risiken</Text>
                    {(dominantStrategy.risks as any[]).slice(0, 2).map((risk: any, idx: number) => {
                      const riskText = typeof risk === 'string' ? risk : risk?.risk;
                      return (
                        <Text key={idx} style={{ fontSize: 7, color: "#92400e", marginBottom: 2 }}>• {truncateText(riskText, 80)}</Text>
                      );
                    })}
                  </View>
                )}
                
                {dominantStrategy.risk_mitigation.length > 0 && (
                  <View style={[styles.successBox, { flex: 1, marginLeft: 6 }]}>
                    <Text style={{ fontSize: 7, fontWeight: "bold", color: "#166534", marginBottom: 4, textTransform: "uppercase" }}>Absicherung</Text>
                    {dominantStrategy.risk_mitigation.slice(0, 2).map((item: string, idx: number) => (
                      <Text key={idx} style={{ fontSize: 7, color: "#166534", marginBottom: 2 }}>• {truncateText(item, 80)}</Text>
                    ))}
                  </View>
                )}
              </View>
            )}
          </View>
        )}

        {/* STRATEGIC PATH */}
        {hasStrategicPathData && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Strategischer Pfad</Text>
            
            <View style={{ flexDirection: "row", marginBottom: 12 }}>
              {/* Current Decision */}
              {strategicPath.current_phase && (
                <View style={{ flex: 1, marginRight: 6 }}>
                  <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", marginBottom: 3 }}>Jetzt</Text>
                  <View style={[styles.highlightBox, { marginBottom: 0 }]}>
                    <Text style={{ fontSize: 8, fontWeight: "bold", color: "#1f2937" }}>
                      {truncateText(strategicPath.current_phase, 100)}
                    </Text>
                  </View>
                </View>
              )}

              {/* Next Decision Point */}
              {strategicPath.next_decision_point && (
                <View style={{ flex: 1, marginLeft: 6 }}>
                  <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", marginBottom: 3 }}>Dann</Text>
                  <View style={[styles.warningBox, { marginBottom: 0 }]}>
                    <Text style={{ fontSize: 8, fontWeight: "bold", color: "#1f2937" }}>
                      {truncateText(strategicPath.next_decision_point, 100)}
                    </Text>
                  </View>
                </View>
              )}
            </View>

            {/* Transition Triggers - Compact */}
            {strategicPath.transition_triggers.length > 0 && (
              <View style={styles.dangerBox}>
                <Text style={{ fontSize: 7, fontWeight: "bold", color: "#991b1b", marginBottom: 4 }}>Strategiewechsel bei:</Text>
                {strategicPath.transition_triggers.slice(0, 3).map((trigger: string, idx: number) => (
                  <Text key={idx} style={{ fontSize: 7, color: "#991b1b", marginBottom: 2 }}>→ {truncateText(trigger, 100)}</Text>
                ))}
              </View>
            )}
          </View>
        )}

        {/* Alternative Options - Very compact */}
        {alternativeOptions.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Alternativen</Text>
            {alternativeOptions.slice(0, 2).map((option: any, idx: number) => (
              option.when_this_option_fits && (
                <View key={idx} style={[styles.snapshotBox, { marginBottom: 4, padding: 6 }]}>
                  <Text style={{ fontSize: 8, fontWeight: "bold", color: "#1f2937", marginBottom: 2 }}>
                    {cleanText(option.label)}
                  </Text>
                  <Text style={{ fontSize: 7, color: "#6b7280" }}>
                    Besser wenn: {truncateText(option.when_this_option_fits, 120)}
                  </Text>
                </View>
              )
            ))}
          </View>
        )}

        <Footer />
      </Page>

      {/* PAGE 4 - Phase 1 Erfolgskriterien (nur operational + wenn Daten vorhanden) */}
      {mode === 'operational' && hasPhase1Data && (
        <Page size="A4" style={styles.page} wrap>
          <View style={styles.header}>
            <Text style={styles.title}>Erfolgskriterien</Text>
            <Text style={styles.subtitle}>Phase 1: Strukturierte Entscheidungsgrundlage</Text>
          </View>

          {/* 1.1 Erfolgskriterien */}
          {(successCriteria.signal || successCriteria.timeframe || successCriteria.validators.length > 0) && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Erfolgssignal</Text>
              
              {/* Signal - 3-tier structure */}
              {successCriteria.signal && (
                <View style={styles.criteriaCard}>
                  <Text style={styles.outcomeDecision}>{truncateText(successCriteria.signal, 150)}</Text>
                  
                  <View style={{ flexDirection: "row", marginTop: 8 }}>
                    {/* Zeitrahmen */}
                    {successCriteria.timeframe && (
                      <View style={{ flex: 1, marginRight: 8 }}>
                        <Text style={styles.criteriaLabel}>Zeitrahmen</Text>
                        <Text style={{ fontSize: 9, color: "#1f2937" }}>{successCriteria.timeframe}</Text>
                      </View>
                    )}

                    {/* Validatoren */}
                    {successCriteria.validators.length > 0 && (
                      <View style={{ flex: 1 }}>
                        <Text style={styles.criteriaLabel}>Validierung durch</Text>
                        <View style={{ flexDirection: "row", flexWrap: "wrap", marginTop: 2 }}>
                          {successCriteria.validators
                            .sort((a: string, b: string) => 
                              (VALIDATOR_LABELS[a]?.priority || 99) - (VALIDATOR_LABELS[b]?.priority || 99)
                            )
                            .map((validator: string, idx: number) => (
                              <View key={idx} style={styles.validatorBadge}>
                                <Text style={{ fontSize: 7, color: primaryColor }}>
                                  {VALIDATOR_LABELS[validator]?.label || validator}
                                </Text>
                              </View>
                            ))}
                        </View>
                      </View>
                    )}
                  </View>
                </View>
              )}
            </View>
          )}

          {/* 1.2 Zielkonflikt - Compact */}
          {(conflictData.strategyA || conflictData.strategyB) && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Strategischer Zielkonflikt</Text>
              
              <View style={{ flexDirection: "row", marginBottom: 8 }}>
                {conflictData.strategyA && (
                  <View style={[styles.conflictBox, { flex: 1, marginRight: 6 }]}>
                    <Text style={{ fontSize: 7, color: "#92400e", marginBottom: 3 }}>Bei Option A aufgegeben:</Text>
                    <Text style={{ fontSize: 8, fontWeight: "bold", color: "#1f2937" }}>{truncateText(conflictData.strategyA, 100)}</Text>
                  </View>
                )}

                {conflictData.strategyB && (
                  <View style={[styles.conflictBox, { flex: 1, marginLeft: 6 }]}>
                    <Text style={{ fontSize: 7, color: "#92400e", marginBottom: 3 }}>Bei Option B aufgegeben:</Text>
                    <Text style={{ fontSize: 8, fontWeight: "bold", color: "#1f2937" }}>{truncateText(conflictData.strategyB, 100)}</Text>
                  </View>
                )}
              </View>

              {/* Irreversible Kosten - Compact Grid */}
              {conflictData.costs && (
                <View style={styles.dangerBox}>
                  <Text style={{ fontSize: 8, fontWeight: "bold", marginBottom: 4, color: "#991b1b" }}>
                    Kosten der Nicht-Entscheidung
                  </Text>
                  <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
                    {conflictData.costs.credibility && (
                      <Text style={{ fontSize: 7, color: "#991b1b", marginRight: 12, marginBottom: 2 }}>
                        Glaubwürdigkeit: {formatCostLevel(conflictData.costs.credibility)}
                      </Text>
                    )}
                    {conflictData.costs.speed && (
                      <Text style={{ fontSize: 7, color: "#991b1b", marginRight: 12, marginBottom: 2 }}>
                        Geschwindigkeit: {formatCostLevel(conflictData.costs.speed)}
                      </Text>
                    )}
                    {conflictData.costs.learning_loss && (
                      <Text style={{ fontSize: 7, color: "#991b1b", marginRight: 12, marginBottom: 2 }}>
                        Lernverlust: {formatCostLevel(conflictData.costs.learning_loss)}
                      </Text>
                    )}
                  </View>
                </View>
              )}
            </View>
          )}

          {/* 1.3 Hypothese - Compact */}
          {hypothesisData.hypothesis && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Zentrale Marktannahme</Text>
              <View style={styles.criteriaCard}>
                <Text style={styles.criteriaValue}>{truncateText(hypothesisData.hypothesis, 200)}</Text>
                {hypothesisData.falsification && (
                  <View style={{ marginTop: 6, paddingTop: 6, borderTop: "1 solid #e5e7eb" }}>
                    <Text style={{ fontSize: 7, color: "#991b1b" }}>
                      Widerlegt wenn: {truncateText(hypothesisData.falsification, 150)}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          )}

          {/* 1.4 Killer-Annahme */}
          {killerAssumptionData.assumption && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Killer-Annahme</Text>
              <View style={[styles.criteriaCard, { borderLeftColor: "#ef4444" }]}>
                <Text style={styles.criteriaValue}>{truncateText(killerAssumptionData.assumption, 200)}</Text>
                {killerAssumptionData.falsification && (
                  <View style={{ marginTop: 6, paddingTop: 6, borderTop: "1 solid #e5e7eb" }}>
                    <Text style={{ fontSize: 7, color: "#991b1b" }}>
                      Widerlegt wenn: {truncateText(killerAssumptionData.falsification, 150)}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          )}

          <Footer />
        </Page>
      )}

      {/* PAGE 5 - Commitment & Action Plan */}
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header}>
          <Text style={styles.title}>Umsetzung</Text>
          <Text style={styles.subtitle}>Verbindlichkeit und Aktionsplan</Text>
        </View>

        {/* Commitment Block */}
        {(commitment.committed_at || commitment.committed_option_id || commitment.owner_name || commitment.owner) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Verbindliche Entscheidung</Text>
            <View style={styles.commitmentBox}>
              <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 6 }}>
                <View>
                  <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase" }}>Verantwortung</Text>
                  <Text style={{ fontSize: 10, fontWeight: "bold" }}>{commitment.owner_name || commitment.owner || "–"}</Text>
                </View>
                <View>
                  <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase" }}>Gültigkeit</Text>
                  <Text style={{ fontSize: 10, fontWeight: "bold" }}>{commitment.validity_days || 30} Tage</Text>
                </View>
                <View>
                  <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase" }}>Review</Text>
                  <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                    {commitment.review_date ? new Date(commitment.review_date).toLocaleDateString("de-DE") : "–"}
                  </Text>
                </View>
              </View>
              {commitment.committed_option_name && (
                <Text style={{ fontSize: 9, fontWeight: "bold", color: primaryColor, marginTop: 4 }}>
                  Gewählt: {cleanText(commitment.committed_option_name)}
                </Text>
              )}
            </View>

            {/* Bewusster Verzicht - Compact */}
            {Array.isArray(commitment.excluded_options) && commitment.excluded_options.length > 0 && (
              <View style={{ marginTop: 6 }}>
                <Text style={{ fontSize: 8, fontWeight: "bold", marginBottom: 4, color: "#6b7280" }}>Bewusster Verzicht:</Text>
                {commitment.excluded_options.map((excluded: any, idx: number) => (
                  <View key={idx} style={styles.excludedBox}>
                    <Text style={{ fontSize: 7, fontWeight: "bold" }}>{cleanText(excluded.label)}</Text>
                    <Text style={{ fontSize: 6, color: "#6b7280" }}>{truncateText(excluded.consequence, 100)}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        )}

        {/* Action Roadmap - 30 days */}
        {weeks.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>30-Tage-Aktionsplan</Text>
            {weeks.map((week: any, idx: number) => (
              <View key={idx} style={styles.weekBox} wrap={false}>
                <Text style={styles.weekTitle}>Woche {idx + 1}: {cleanText(week?.focus) || ""}</Text>
                
                {Array.isArray(week?.tasks) && week.tasks.slice(0, 4).map((task: string, tIdx: number) => (
                  <Text key={tIdx} style={styles.taskItem}>☐ {cleanText(task)}</Text>
                ))}
                
                {Array.isArray(week?.kpis) && week.kpis.length > 0 && (
                  <View style={{ marginTop: 3 }}>
                    {week.kpis.slice(0, 2).map((kpi: string, kIdx: number) => (
                      <Text key={kIdx} style={styles.kpiItem}>KPI: {cleanText(kpi)}</Text>
                    ))}
                  </View>
                )}
                
                {week?.decision_point && (
                  <Text style={{ fontSize: 7, color: "#6366f1", marginTop: 3, fontStyle: "italic" }}>
                    Entscheidungspunkt: {cleanText(week.decision_point)}
                  </Text>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Hypotheses to Test */}
        {hypothesesToTest.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Hypothesen zum Testen</Text>
            <View style={[styles.highlightBox, { backgroundColor: "#fef3c7", borderLeftColor: "#f59e0b" }]}>
              {hypothesesToTest.slice(0, 4).map((hypothesis: string, idx: number) => (
                <View key={idx} style={{ flexDirection: "row", marginBottom: 3 }}>
                  <Text style={{ fontSize: 8, fontWeight: "bold", color: "#92400e", marginRight: 6 }}>{idx + 1}.</Text>
                  <Text style={{ fontSize: 8, color: "#1f2937", flex: 1, lineHeight: 1.5 }}>{hypothesis}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Decision Thresholds - Compact Grid */}
        {actionRoadmap?.decision_thresholds && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Entscheidungskriterien</Text>
            {actionRoadmap.decision_thresholds.primary_kpi && (
              <View style={[styles.highlightBox, { marginBottom: 8 }]}>
                <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", marginBottom: 2 }}>North-Star Metrik</Text>
                <Text style={{ fontSize: 10, fontWeight: "bold", color: "#1f2937" }}>
                  {cleanText(actionRoadmap.decision_thresholds.primary_kpi)}
                </Text>
              </View>
            )}
            <View style={{ flexDirection: "row" }}>
              <View style={[styles.thresholdBox, { backgroundColor: "#dcfce7" }]}>
                <Text style={{ fontSize: 8, fontWeight: "bold", color: "#166534" }}>GO</Text>
                <Text style={{ fontSize: 7, color: "#166534", marginTop: 2 }}>{cleanText(actionRoadmap.decision_thresholds.go) || "–"}</Text>
              </View>
              <View style={[styles.thresholdBox, { backgroundColor: "#fef3c7" }]}>
                <Text style={{ fontSize: 8, fontWeight: "bold", color: "#92400e" }}>ITERATE</Text>
                <Text style={{ fontSize: 7, color: "#92400e", marginTop: 2 }}>{cleanText(actionRoadmap.decision_thresholds.iterate) || "–"}</Text>
              </View>
              <View style={[styles.thresholdBox, { backgroundColor: "#fee2e2" }]}>
                <Text style={{ fontSize: 8, fontWeight: "bold", color: "#991b1b" }}>PIVOT</Text>
                <Text style={{ fontSize: 7, color: "#991b1b", marginTop: 2 }}>{cleanText(actionRoadmap.decision_thresholds.pivot) || "–"}</Text>
              </View>
            </View>
          </View>
        )}

        <Footer />
      </Page>

      {/* PAGE 6 - Failure Analysis & Go Decision (operational only, only if content exists) */}
      {mode === 'operational' && ((Array.isArray(failureAnalysis?.failure_indicators) && failureAnalysis.failure_indicators.length > 0) || 
        (Array.isArray(goDecision?.items) && goDecision.items.length > 0)) && (
        <Page size="A4" style={styles.page} wrap>
          <View style={styles.header}>
            <Text style={styles.title}>Risikomanagement</Text>
            <Text style={styles.subtitle}>Warnsignale und Abbruchkriterien</Text>
          </View>

          {/* Failure Analysis */}
          {Array.isArray(failureAnalysis?.failure_indicators) && failureAnalysis.failure_indicators.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Warnsignale</Text>
              
              <View style={styles.dangerBox}>
                <Text style={{ fontSize: 8, fontWeight: "bold", marginBottom: 4 }}>Frühe Indikatoren</Text>
                {failureAnalysis.failure_indicators.slice(0, 4).map((indicator: string, idx: number) => (
                  <Text key={idx} style={{ fontSize: 7, marginBottom: 2 }}>⚠ {cleanText(indicator)}</Text>
                ))}
              </View>

              {Array.isArray(failureAnalysis?.pivot_triggers) && failureAnalysis.pivot_triggers.length > 0 && (
                <View style={[styles.warningBox, { marginTop: 6 }]}>
                  <Text style={{ fontSize: 8, fontWeight: "bold", marginBottom: 4 }}>Pivot-Trigger</Text>
                  {failureAnalysis.pivot_triggers.slice(0, 3).map((trigger: string, idx: number) => (
                    <Text key={idx} style={{ fontSize: 7, marginBottom: 2 }}>→ {cleanText(trigger)}</Text>
                  ))}
                </View>
              )}

              {failureAnalysis?.abort_criteria && (
                <View style={[styles.dangerBox, { marginTop: 6, backgroundColor: "#fecaca" }]}>
                  <Text style={{ fontSize: 8, fontWeight: "bold", color: "#991b1b" }}>
                    Abbruch: {cleanText(failureAnalysis.abort_criteria)}
                  </Text>
                </View>
              )}
            </View>
          )}

          {/* Go Decision Check */}
          {Array.isArray(goDecision?.items) && goDecision.items.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Go-Decision Checkliste</Text>
              {goDecision.items.map((item: any, idx: number) => (
                <View key={idx} style={[styles.successBox, { flexDirection: "row", alignItems: "center" }]}>
                  <Text style={{ fontSize: 10, marginRight: 6 }}>{item?.checked ? "✓" : "○"}</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 8, fontWeight: "bold" }}>{cleanText(item?.label) || ""}</Text>
                    {item?.description && (
                      <Text style={{ fontSize: 7, color: "#6b7280" }}>{truncateText(item.description, 100)}</Text>
                    )}
                  </View>
                </View>
              ))}
              {goDecision?.overall_ready !== undefined && (
                <Text style={{ 
                  fontSize: 9, 
                  fontWeight: "bold", 
                  textAlign: "center", 
                  marginTop: 8, 
                  color: goDecision.overall_ready ? "#166534" : "#92400e" 
                }}>
                  {goDecision.overall_ready ? "✓ Bereit zur Umsetzung" : "⚠ Noch nicht bereit"}
                </Text>
              )}
            </View>
          )}

          <Footer />
        </Page>
      )}

      {/* PAGE 7 - Team Perspectives (operational only, only if multi-user data exists) */}
      {mode === 'operational' && hasTeamPerspectives && (
        <Page size="A4" style={styles.page} wrap>
          <View style={styles.header}>
            <Text style={styles.title}>Team-Perspektiven</Text>
            <Text style={styles.subtitle}>Übereinstimmung, Divergenzen und blockierende Themen</Text>
          </View>

          {teamPerspectives.synthesis && (
            <View style={[styles.metaContext, { marginBottom: 12 }]}>
              <Text style={{ fontSize: 8, color: "#4b5563", lineHeight: 1.6 }}>
                {cleanText(teamPerspectives.synthesis)}
              </Text>
            </View>
          )}

          {/* Consensus */}
          {Array.isArray(teamPerspectives.consensus_areas) && teamPerspectives.consensus_areas.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Übereinstimmung</Text>
              {teamPerspectives.consensus_areas.map((area: string, idx: number) => (
                <View key={idx} style={styles.successBox}>
                  <Text style={{ fontSize: 8, color: "#166534" }}>✓ {cleanText(area)}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Divergence */}
          {Array.isArray(teamPerspectives.divergence_areas) && teamPerspectives.divergence_areas.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Unterschiedliche Sichtweisen</Text>
              {teamPerspectives.divergence_areas.map((div: any, idx: number) => (
                <View key={idx} style={styles.warningBox}>
                  <Text style={{ fontSize: 8, fontWeight: "bold", marginBottom: 2 }}>
                    {cleanText(div.topic)}
                    {div.user_a && div.user_b ? ` — ${cleanText(div.user_a)} vs. ${cleanText(div.user_b)}` : ""}
                  </Text>
                  <Text style={{ fontSize: 7, color: "#92400e" }}>{cleanText(div.description)}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Blocking Issues */}
          {Array.isArray(teamPerspectives.blocking_issues) && teamPerspectives.blocking_issues.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Blockierende Themen</Text>
              {teamPerspectives.blocking_issues.map((issue: string, idx: number) => (
                <View key={idx} style={styles.dangerBox}>
                  <Text style={{ fontSize: 8, color: "#991b1b" }}>✗ {cleanText(issue)}</Text>
                </View>
              ))}
            </View>
          )}

          <Footer />
        </Page>
      )}

      {/* PAGE 8 - Annahmen & Offene Punkte (operational only, nur wenn Daten vorhanden) */}
      {mode === 'operational' && (() => {
        const assumptions = metadata?.assumptions_aggregated || [];
        const openQuestions = metadata?.open_questions || [];
        const dataQuality = metadata?.data_quality;
        if ((!Array.isArray(assumptions) || assumptions.length === 0) && 
            (!Array.isArray(openQuestions) || openQuestions.length === 0) && 
            !dataQuality) return null;
        return (
          <Page size="A4" style={styles.page} wrap>
            <View style={styles.header}>
              <Text style={styles.title}>Annahmen & Offene Punkte</Text>
              <Text style={styles.subtitle}>Transparenz der Entscheidungsgrundlage</Text>
            </View>

            {/* Annahmen */}
            {Array.isArray(assumptions) && assumptions.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Annahmen-Übersicht</Text>
                {assumptions.map((item: any, idx: number) => (
                  <View key={idx} style={[styles.snapshotBox, { marginBottom: 4, padding: 6 }]}>
                    <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 2 }}>
                      <Text style={{ fontSize: 8, fontWeight: "bold", color: "#1f2937", flex: 1 }}>
                        {cleanText(item.option_label || item.label || `Annahme ${idx + 1}`)}
                      </Text>
                      {item.status && (
                        <View style={{ backgroundColor: item.status === 'validated' ? "#dcfce7" : item.status === 'rejected' ? "#fee2e2" : "#f3f4f6", padding: 2, borderRadius: 2 }}>
                          <Text style={{ fontSize: 6, color: item.status === 'validated' ? "#166534" : item.status === 'rejected' ? "#991b1b" : "#6b7280" }}>
                            {item.status === 'validated' ? "Bestätigt" : item.status === 'rejected' ? "Widerlegt" : "Offen"}
                          </Text>
                        </View>
                      )}
                    </View>
                    {item.assumption && (
                      <Text style={{ fontSize: 7, color: "#4b5563", lineHeight: 1.5 }}>{truncateText(item.assumption, 150)}</Text>
                    )}
                  </View>
                ))}
              </View>
            )}

            {/* Offene Punkte */}
            {Array.isArray(openQuestions) && openQuestions.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Offene Punkte / Fehlende Daten</Text>
                <View style={styles.warningBox}>
                  {openQuestions.map((q: any, idx: number) => {
                    const text = typeof q === 'string' ? q : q?.question || q?.text || '';
                    return (
                      <Text key={idx} style={{ fontSize: 7, color: "#92400e", marginBottom: 2, lineHeight: 1.5 }}>
                        • {cleanText(text)}
                      </Text>
                    );
                  })}
                </View>
              </View>
            )}

            {/* Datenqualität */}
            {dataQuality != null && (
              <View style={[styles.metaContext, { marginTop: 8 }]}>
                <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                  <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase" }}>Datenqualitäts-Score</Text>
                  <Text style={{ fontSize: 10, fontWeight: "bold", color: typeof dataQuality === 'number' && dataQuality >= 70 ? "#166534" : "#92400e" }}>
                    {typeof dataQuality === 'number' ? `${dataQuality}%` : String(dataQuality)}
                  </Text>
                </View>
              </View>
            )}

            <Footer />
          </Page>
        );
      })()}

      {/* REPRODUCIBILITY PAGE - Input Hash & Model Version */}
      {mode === 'operational' && (inputsHash || modelVersion) && (
        <Page size="A4" style={styles.page} wrap>
          <View style={styles.header}>
            <Text style={styles.title}>Reproduzierbarkeit</Text>
            <Text style={styles.subtitle}>Nachvollziehbarkeit der Entscheidungsgrundlage</Text>
          </View>

          <View style={styles.metaContext}>
            {inputsHash && (
              <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 4 }}>
                <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>Inputs-Hash</Text>
                <Text style={{ fontSize: 8, color: "#1f2937", fontFamily: "Courier" }}>{inputsHash.substring(0, 16)}</Text>
              </View>
            )}
            {modelVersion && (
              <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 4 }}>
                <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>Modell</Text>
                <Text style={{ fontSize: 8, color: "#1f2937" }}>{modelVersion}</Text>
              </View>
            )}
            <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 4 }}>
              <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>Temperatur / Seed</Text>
              <Text style={{ fontSize: 8, color: "#1f2937" }}>0 / 42</Text>
            </View>
            {reportId && (
              <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 4 }}>
                <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>Report-ID</Text>
                <Text style={{ fontSize: 8, color: "#1f2937", fontFamily: "Courier" }}>{reportId.substring(0, 12)}</Text>
              </View>
            )}
            {commitHash && (
              <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                <Text style={{ fontSize: 7, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>Commit-Hash</Text>
                <Text style={{ fontSize: 8, color: "#1f2937", fontFamily: "Courier" }}>{commitHash}</Text>
              </View>
            )}
          </View>

          <View style={{ marginTop: 12 }}>
            <Text style={{ fontSize: 7, color: "#9ca3af", lineHeight: 1.6 }}>
              Dieser Report wurde deterministisch aus den oben referenzierten Inputs generiert. Bei identischen Eingabedaten und identischem Modell wird exakt dasselbe Ergebnis erzeugt.
            </Text>
          </View>

          <Footer />
        </Page>
      )}
    </Document>
  );
};
